import { useEffect, useRef, useState } from 'preact/hooks';
import type { Action } from '../engine';
import { RESOURCES, type GameView, type Resources } from '../engine';
import { sounds } from './audio';
import type { GameClient } from './game-client';

function countPieces(view: GameView): number {
  return view.buildings.filter(Boolean).length + view.roads.filter((r) => r !== null).length;
}

function citiesOf(view: GameView): number {
  return view.buildings.filter((b) => b?.kind === 'city').length;
}

/**
 * Plays sounds for changes between consecutive views. Diffing views (rather
 * than consuming events) means hot-seat and online play behave the same.
 */
export function useGameSounds(client: GameClient): void {
  const prev = useRef<{ view: GameView; myMove: boolean } | null>(null);
  const { view } = client;
  const myMove = !client.hotSeat && client.legal.length > 0 && view.phase.kind !== 'gameOver';

  useEffect(() => {
    const before = prev.current;
    prev.current = { view, myMove };
    if (!before) return;
    const b = before.view;
    // One roll per turn, so "had no roll, or a different turn" means a fresh roll.
    if (view.lastRoll && (b.lastRoll === null || view.turn !== b.turn)) {
      sounds.dice();
    } else if (countPieces(view) > countPieces(b) || citiesOf(view) > citiesOf(b)) {
      sounds.build();
    } else if (view.trade && view.trade.id !== b.trade?.id) {
      sounds.trade();
    }
    if (view.phase.kind === 'gameOver' && b.phase.kind !== 'gameOver') sounds.win();
    if (myMove && !before.myMove) {
      sounds.yourTurn();
      try {
        navigator.vibrate?.(120);
      } catch {
        // Not supported; the chime is enough.
      }
    }
  }, [view, myMove]);
}

/** Per-resource change since the previous hand (negative for losses), with a counter to retrigger the animation. */
export function useHandDeltas(hand: Resources | null, seat: number): { deltas: Resources; key: number } {
  const prev = useRef<Resources | null>(hand);
  const prevSeat = useRef(seat);
  const [state, setState] = useState<{ deltas: Resources; key: number }>({
    deltas: { brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 },
    key: 0,
  });
  useEffect(() => {
    const before = prev.current;
    prev.current = hand;
    // A hot-seat handoff swaps whose hand this is; that's not a gain.
    const sameSeat = prevSeat.current === seat;
    prevSeat.current = seat;
    if (!before || !hand || !sameSeat) return;
    const deltas = { brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 };
    let any = false;
    for (const r of RESOURCES) {
      deltas[r] = hand[r] - before[r];
      if (deltas[r] !== 0) any = true;
    }
    if (any) setState((s) => ({ deltas, key: s.key + 1 }));
  }, [seat, hand && RESOURCES.map((r) => hand[r]).join(',')]);
  return state;
}

/**
 * Seconds left in a soft turn timer that restarts whenever `turnKey` changes.
 * It's per-device and advisory: nothing happens at zero except the display.
 */
export function useTurnClock(turnKey: string, seconds: number | null | undefined): number | null {
  const started = useRef({ key: turnKey, at: Date.now() });
  const [, tick] = useState(0);
  if (started.current.key !== turnKey) started.current = { key: turnKey, at: Date.now() };
  useEffect(() => {
    if (!seconds) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [seconds]);
  if (!seconds) return null;
  return Math.max(0, seconds - Math.floor((Date.now() - started.current.at) / 1000));
}

export interface AutoPlay {
  roll: boolean;
  end: boolean;
}

const AUTO_KEY = 'hexagon.auto';

/** Per-device auto-roll / auto-end preferences. */
export function useAutoPlay(): [AutoPlay, (next: AutoPlay) => void] {
  const [auto, setAuto] = useState<AutoPlay>(() => {
    try {
      return { roll: false, end: false, ...(JSON.parse(localStorage.getItem(AUTO_KEY) ?? '{}') as Partial<AutoPlay>) };
    } catch {
      return { roll: false, end: false };
    }
  });
  const save = (next: AutoPlay) => {
    setAuto(next);
    try {
      localStorage.setItem(AUTO_KEY, JSON.stringify(next));
    } catch {
      // Private mode: the choice lasts for this page only.
    }
  };
  return [auto, save];
}

/** Anything left to do this turn besides ending it (building, buying, bank trades, playing a card). */
const PRODUCTIVE: ReadonlySet<Action['type']> = new Set([
  'buildRoad',
  'buildSettlement',
  'buildCity',
  'buyDevCard',
  'bankTrade',
  'playKnight',
  'playRoadBuilding',
  'playYearOfPlenty',
  'playMonopoly',
]);

/**
 * The move auto-play would make now, or null: roll at the start of the turn
 * (unless a Knight could be played first), and end the turn once nothing
 * productive is left and no trade is open.
 */
export function autoMove(auto: AutoPlay, legal: readonly Action[], tradeOpen: boolean): Action | null {
  const has = (t: Action['type']) => legal.some((a) => a.type === t);
  if (auto.roll && has('rollDice') && !has('playKnight')) return { type: 'rollDice' };
  if (auto.end && has('endTurn') && !tradeOpen && !legal.some((a) => PRODUCTIVE.has(a.type))) return { type: 'endTurn' };
  return null;
}
